import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import ImageFrame from "@/components/site/ImageFrame";
import Reveal from "@/components/site/Reveal";
import SectionHeading from "@/components/site/SectionHeading";
import Seo from "@/components/site/Seo";
import PageHeader from "@/components/site/PageHeader";

export default function Cucina() {
  const { settings } = useSettings();
  return (
    <>
      <Seo
        title="La cucina · Osteria Aterrana, cucina irpina e campana a Montoro"
        description="Cucina tradizionale irpina e campana: Genovese, pasta e fagioli con le cozze, parmigiana, carni alla brace. Prodotti scelti con cura, nel borgo di Aterrana."
        path="/la-cucina"
      />
      <PageHeader
        eyebrow="La cucina"
        title="Poche cose, fatte come si deve"
        lead="Cucina campana e irpina: la genovese con la cipolla ramata di Montoro, il ragù, la parmigiana, il baccalà, le alici, il mallone."
        label="CHEF / KITCHEN PHOTO"
        alt="Un piatto della cucina dell'Osteria Aterrana"
        src={settings.cucina_header_image}
      />

      <section className="section-y bg-cream">
        <div className="container-ed grid gap-16 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <SectionHeading eyebrow="Il modo di lavorare" title="Senza scorciatoie" />
          </div>
          <Reveal className="lg:col-span-6 lg:col-start-7 prose-ed text-sm md:text-lg text-foreground/80">
            <p>
              I sughi cuociono il tempo che serve, i dolci si fanno in casa, le verdure si scelgono
              quando è stagione. Non abbiamo una carta lunga: preferiamo pochi piatti e farli bene.
            </p>
            <p>
              La genovese resta sul fuoco per ore, con la cipolla ramata di Montoro che cresce a due
              passi da qui. Il ragù cuoce piano. Il baccalà, le alici fritte e il mallone sono ricette
              di casa: nessun effetto speciale, solo il rispetto della ricetta e del prodotto.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-beige/50 py-24 md:py-32">
        <div className="container-ed grid gap-10 md:grid-cols-12">
          <Reveal className="md:col-span-5">
            <ImageFrame
              src={settings.cucina_image_1}
              label="DISH PHOTO"
              alt="Un piatto dell'osteria con erbe fresche"
              ratio="3 / 4"
            />
          </Reveal>
          <div className="md:col-span-6 md:col-start-7 self-center">
            <SectionHeading
              eyebrow="Le materie prime"
              title={settings.product_title || "Prima del piatto, viene il prodotto"}
            />
            <Reveal delay={0.1} className="mt-7 prose-ed text-sm md:text-lg text-foreground/78">
              {(settings.product_text || "").split("\n\n").map((p) => (
                <p key={p.slice(0, 60)}>{p}</p>
              ))}
            </Reveal>
          </div>
        </div>
      </section>

      <section className="section-y bg-cream">
        <div className="container-ed">
          <SectionHeading eyebrow="Sul tavolo" title="Alcune cose che troverai" />
      <p className="mt-3 text-sm text-foreground/55">
        Storicamente questi spazi erano conosciuti nel borgo come la Cantina dei Briganti.
      </p>
      <div className="mt-14 grid gap-x-14 gap-y-12 md:grid-cols-3">
            {[
              ["Genovese", "Manzo e cipolla ramata di Montoro, cottura lunghissima."],
              ["Ragù", "Cotto piano, come si fa in queste case."],
              ["Parmigiana", "Melanzane fritte, pomodoro, fiordilatte, basilico."],
              ["Baccalà", "In umido con pomodorini, olive e patate."],
              ["Alici e mallone", "Alici fritte al momento; mallone di verdure di campo e patate."],
              ["Dolci di casa", "Crostate e pastiera quando è tempo."],
            ].map(([t, d], i) => (
              <Reveal key={t} delay={i * 0.06} className="border-t border-wood/15 pt-6">
                <h3 className="font-serif text-2xl text-wood">{t}</h3>
                <p className="mt-2 text-sm text-foreground/65 leading-relaxed">{d}</p>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-16 flex flex-wrap gap-4">
            <Link
              to="/menu"
              className="inline-flex items-center gap-2 px-8 py-4 bg-wood text-cream text-[0.7rem] uppercase tracking-[0.22em] hover:bg-sage transition-colors duration-500"
            >
              Scopri il menu <ArrowRight size={14} strokeWidth={1.4} />
            </Link>
            <Link
              to="/prenota"
              className="inline-flex items-center gap-2 px-8 py-4 border border-wood/30 text-wood text-[0.7rem] uppercase tracking-[0.22em] hover:bg-wood hover:text-cream transition-colors duration-500"
            >
              Prenota un tavolo
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
